const LP_FORM_SHOP_CONFIG = {
  'laatupuhtaus.myshopify.com': { proxyPath: '/apps/lp', storeId: 'lp' },
  'laatupuhtaus-demo.myshopify.com': {
    proxyPath: '/apps/lp',
    storeId: 'lp-demo',
  },
  'w2w-hyvinvointi.myshopify.com': { proxyPath: '/apps/trv', storeId: 'trv' },
  'w2w-demo.myshopify.com': { proxyPath: '/apps/trv', storeId: 'trv-demo' },
  'nts-suomi.myshopify.com': { proxyPath: '/apps/nts', storeId: 'nts' },
  'nts-demo.myshopify.com': { proxyPath: '/apps/nts', storeId: 'nts-demo' },
};

class LPFormComponent extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    window.requestAnimationFrame(() => this.initialize());
  }

  initialize() {
    if (this.initialized) return;

    this.form = this.querySelector('[data-lp-form]');
    this.pagesContainer = this.querySelector('[data-lp-form-pages]');
    this.pages = Array.from(this.querySelectorAll('[data-lp-form-page]'));
    this.previousButton = this.querySelector('[data-lp-form-prev]');
    this.nextButton = this.querySelector('[data-lp-form-next]');
    this.submitButton = this.querySelector('[data-lp-form-submit]');
    this.statusElement = this.querySelector('[data-lp-form-status]');
    this.submitPagesContainer = this.querySelector('[data-lp-submit-pages]');
    this.submitPages = Array.from(
      this.querySelectorAll('[data-lp-submit-page]'),
    );
    this.successPages = this.submitPages.filter(
      (page) => page.dataset.lpSubmitPageType === 'success',
    );
    this.errorPages = this.submitPages.filter(
      (page) => page.dataset.lpSubmitPageType === 'error',
    );
    this.errorMessageElements = Array.from(
      this.querySelectorAll('[data-lp-submit-error-message]'),
    );
    this.calculatorPages = Array.from(
      this.querySelectorAll('[data-lp-calculator-page]'),
    );
    this.calculatorResults = Array.from(
      this.querySelectorAll('[data-lp-calculator-results]'),
    );
    this.currentPage = 0;

    if (!this.form) {
      window.setTimeout(() => this.initialize(), 0);
      return;
    }

    this.initialized = true;

    this.initializeCalculators();
    this.initializeChoiceGroups();
    this.initializeConsents();
    this.initializeNumericInputs();
    this.initializeThemeEditorEvents();
    this.hideSubmitPages();
    this.pages.forEach((page, index) => {
      page.dataset.lpFormPage = String(index + 1);
    });
    this.setPage(0);

    this.previousButton?.addEventListener('click', () =>
      this.setPage(this.currentPage - 1),
    );
    this.nextButton?.addEventListener('click', () => {
      if (this.validateCurrentPage()) this.setPage(this.currentPage + 1);
    });
    this.form.addEventListener('submit', (event) => this.onSubmit(event));
    this.form.addEventListener('input', () => {
      this.recalculateCalculators();
      this.updateConsentState();
    });
    this.form.addEventListener('change', () => {
      this.recalculateCalculators();
      this.updateConsentState();
    });
  }

  initializeCalculators() {
    this.calculators = this.calculatorPages
      .map((page) => this.readCalculator(page))
      .filter(Boolean);

    for (const calculator of this.calculators) {
      this.renderCalculatorFields(calculator);
    }

    this.recalculateCalculators();
  }

  readCalculator(page) {
    const configElement = page.querySelector('[data-lp-calculator-config]');
    if (!configElement?.textContent?.trim()) return null;

    try {
      const config = JSON.parse(configElement.textContent);
      config.fields = this.parseCalculatorJsonValue(config.fields, []);
      config.formulas = this.parseCalculatorJsonValue(config.formulas, []);
      config.conditions = this.parseCalculatorJsonValue(config.conditions, []);
      config.confirmations = this.parseCalculatorJsonValue(config.confirmations, []);
      return {
        config,
        page,
        fieldsElement: page.querySelector('[data-lp-calculator-fields]'),
        values: {},
        calculatedValues: {},
      };
    } catch (error) {
      console.error('Invalid calculator config', error);
      return null;
    }
  }

  parseCalculatorJsonValue(value, fallback) {
    if (Array.isArray(value) || (value && typeof value === 'object')) return value;
    if (typeof value !== 'string' || value.trim() === '') return fallback;

    try {
      return JSON.parse(value);
    } catch {
      return fallback;
    }
  }

  renderCalculatorFields(calculator) {
    if (!calculator.fieldsElement) return;

    calculator.fieldsElement.innerHTML = '';

    for (const field of calculator.config.fields || []) {
      if (field.type === 'result') continue;
      const element = this.renderCalculatorField(field);
      if (element) calculator.fieldsElement.append(element);
    }
  }

  renderCalculatorField(field) {
    if (field.type === 'hidden') {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = field.key;
      input.value = field.defaultValue ?? '';
      this.decorateCalculatorField(input, field);
      return input;
    }

    if (field.type === 'section') {
      return this.renderCalculatorSection(field);
    }

    if (field.type === 'html') {
      const content = document.createElement('div');
      content.className = 'lp-form__html rte';
      content.dataset.lpCalculatorFieldWrapper = field.key;
      content.innerHTML = field.content || '';
      return content;
    }

    if (field.type === 'radio' || field.type === 'checkbox') {
      return this.renderCalculatorChoiceGroup(field);
    }

    if (field.type === 'select') {
      return this.renderCalculatorSelect(field);
    }

    if (field.type === 'textarea') {
      return this.renderCalculatorTextarea(field);
    }

    if (field.type === 'slider') {
      return this.renderCalculatorSlider(field);
    }

    return this.renderCalculatorInput(field);
  }

  renderCalculatorSection(field) {
    const section = document.createElement('div');
    section.className = 'lp-form__calculator-section';
    section.dataset.lpCalculatorFieldWrapper = field.key;

    const heading = document.createElement('h3');
    heading.className = 'lp-form__calculator-section-heading';
    heading.textContent = field.label || field.key;

    section.append(heading);
    return section;
  }

  decorateCalculatorField(control, field) {
    control.dataset.lpField = 'true';
    control.dataset.lpCalculatorField = 'true';
    control.dataset.lpFieldKey = field.key;
    control.dataset.lpFieldLabel = field.label || field.key;
    control.dataset.lpFieldType = field.type;
    control.dataset.lpFieldRequired = String(Boolean(field.required));
    control.required = Boolean(field.required);
  }

  renderCalculatorLabelText(field, className = 'lp-form__label') {
    const label = document.createElement('span');
    label.className = className;
    label.textContent = field.label || field.key;

    if (field.required) {
      const required = document.createElement('span');
      required.className = 'lp-form__required';
      required.setAttribute('aria-hidden', 'true');
      required.textContent = ' *';
      label.append(required);
    }

    return label;
  }

  renderCalculatorInput(field) {
    const label = document.createElement('label');
    label.className = 'lp-form__field';
    label.dataset.lpCalculatorFieldWrapper = field.key;

    const input = document.createElement('input');
    input.className = 'lp-form__input';
    input.type = this.getCalculatorInputType(field.type);
    input.name = field.key;
    input.value = field.defaultValue ?? '';
    if (field.placeholder) input.placeholder = field.placeholder;
    this.decorateCalculatorField(input, field);

    label.append(this.renderCalculatorLabelText(field), input);
    return label;
  }

  renderCalculatorTextarea(field) {
    const label = document.createElement('label');
    label.className = 'lp-form__field';
    label.dataset.lpCalculatorFieldWrapper = field.key;

    const textarea = document.createElement('textarea');
    textarea.className = 'lp-form__input lp-form__textarea';
    textarea.name = field.key;
    textarea.value = field.defaultValue ?? '';
    if (field.placeholder) textarea.placeholder = field.placeholder;
    this.decorateCalculatorField(textarea, field);

    label.append(this.renderCalculatorLabelText(field), textarea);
    return label;
  }

  renderCalculatorSlider(field) {
    const wrapper = document.createElement('label');
    wrapper.className = 'lp-form__field lp-form__slider-field';
    wrapper.dataset.lpCalculatorFieldWrapper = field.key;

    const controls = document.createElement('div');
    controls.className = 'lp-form__slider-controls';

    const range = document.createElement('input');
    range.className = 'lp-form__slider-range';
    range.type = 'range';
    range.min = field.min ?? '0';
    range.max = field.max ?? '100';
    range.step = field.step ?? '1';

    const number = document.createElement('input');
    number.className = 'lp-form__input lp-form__slider-number';
    number.type = 'number';
    number.name = field.key;
    number.min = range.min;
    number.max = range.max;
    number.step = range.step;
    number.value = field.defaultValue || range.min;
    if (field.placeholder) number.placeholder = field.placeholder;
    this.decorateCalculatorField(number, field);

    range.value = number.value;
    range.addEventListener('input', () => {
      number.value = range.value;
      number.dispatchEvent(new Event('input', { bubbles: true }));
    });
    number.addEventListener('input', () => {
      range.value = number.value;
    });

    controls.append(range, number);
    wrapper.append(this.renderCalculatorLabelText(field), controls);
    return wrapper;
  }

  renderCalculatorSelect(field) {
    const label = document.createElement('label');
    label.className = 'lp-form__field';
    label.dataset.lpCalculatorFieldWrapper = field.key;

    const select = document.createElement('select');
    select.className = 'lp-form__input';
    select.name = field.key;
    this.decorateCalculatorField(select, field);

    for (const optionConfig of field.options || []) {
      const option = document.createElement('option');
      option.value = String(optionConfig.value);
      option.textContent = optionConfig.label;
      option.selected = Boolean(optionConfig.selected);
      select.append(option);
    }

    label.append(this.renderCalculatorLabelText(field), select);
    return label;
  }

  renderCalculatorChoiceGroup(field) {
    const fieldset = document.createElement('fieldset');
    fieldset.className = 'lp-form__choice-group';
    fieldset.dataset.lpCalculatorFieldWrapper = field.key;
    fieldset.dataset.lpChoiceGroup = 'true';
    fieldset.dataset.lpChoiceType = field.type;
    fieldset.dataset.lpFieldKey = field.key;
    fieldset.dataset.lpFieldLabel = field.label || field.key;
    fieldset.dataset.lpFieldRequired = String(Boolean(field.required));

    const legend = document.createElement('legend');
    legend.className = 'lp-form__legend';
    legend.append(this.renderCalculatorLabelText(field, ''));
    fieldset.append(legend);

    const options = document.createElement('div');
    options.className = 'lp-form__choice-options';

    for (const [index, optionConfig] of (field.options || []).entries()) {
      const label = document.createElement('label');
      label.className = 'lp-form__choice-label';

      const input = document.createElement('input');
      input.className = 'lp-form__choice-input';
      input.type = field.type;
      input.name = field.type === 'checkbox' ? `${field.key}[]` : field.key;
      input.value = String(optionConfig.value);
      input.checked = Boolean(optionConfig.selected);
      input.dataset.lpChoice = 'true';
      input.dataset.lpCalculatorChoiceKey = `${field.key}.${index + 1}`;
      this.decorateCalculatorField(input, field);
      input.required = false;

      const text = document.createElement('span');
      text.textContent = optionConfig.label;

      label.append(input, text);
      options.append(label);
    }

    fieldset.append(options);
    return fieldset;
  }

  getCalculatorInputType(type) {
    if (type === 'email') return 'email';
    if (type === 'phone') return 'tel';
    if (type === 'number') return 'number';
    if (type === 'date') return 'date';
    if (type === 'time') return 'time';
    if (type === 'datetime' || type === 'datetime-local') return 'datetime-local';
    return 'text';
  }

  recalculateCalculators() {
    if (!this.calculators?.length) return;

    for (const calculator of this.calculators) {
      calculator.values = this.serializeCalculatorValues(calculator.page);
      this.applyCalculatorConditions(calculator);
      calculator.values = this.serializeCalculatorValues(calculator.page);
      calculator.calculatedValues = this.evaluateCalculatorFormulas(calculator);
    }

    this.renderCalculatorResults();
    this.dispatchEvent(
      new CustomEvent('lp-form:calculator:update', {
        bubbles: true,
        detail: { calculators: this.calculators.map(({ config, calculatedValues }) => ({ config, calculatedValues })) },
      }),
    );
  }

  serializeCalculatorValues(scope) {
    const values = {};
    const controls = Array.from(scope.querySelectorAll('[data-lp-calculator-field]'));

    for (const control of controls) {
      const key = control.dataset.lpFieldKey || control.name;
      if (!key || control.disabled) continue;

      if (control.type === 'checkbox') {
        if (!Array.isArray(values[key])) values[key] = [];
        if (control.checked) {
          values[key].push(control.value);
          if (control.dataset.lpCalculatorChoiceKey) {
            values[control.dataset.lpCalculatorChoiceKey] = control.value;
          }
        }
        continue;
      }

      if (control.type === 'radio') {
        if (control.checked) values[key] = control.value;
        if (values[key] === undefined) values[key] = '';
        continue;
      }

      values[key] = control.value;
    }

    return values;
  }

  applyCalculatorConditions(calculator) {
    for (const field of calculator.config.fields || []) {
      if (!field.conditionId) continue;
      const wrapper = calculator.page.querySelector(
        `[data-lp-calculator-field-wrapper="${CSS.escape(field.key)}"]`,
      );
      if (!wrapper) continue;

      const visible = this.evaluateCalculatorCondition(calculator, field.conditionId);
      wrapper.toggleAttribute('hidden', !visible);
      for (const control of wrapper.querySelectorAll('input, select, textarea')) {
        control.disabled = !visible;
      }
    }
  }

  evaluateCalculatorFormulas(calculator) {
    const calculated = {};

    for (const formula of calculator.config.formulas || []) {
      const isVisible =
        !formula.conditionId ||
        this.evaluateCalculatorCondition(calculator, formula.conditionId);
      const value = isVisible
        ? this.evaluateCalculatorMathExpression(formula.expression, {
            ...calculator.values,
            ...calculated,
          })
        : 0;
      calculated[formula.key] = value;

      const hidden = calculator.page.querySelector(
        `[data-lp-calculator-field][data-lp-field-key="${CSS.escape(formula.key)}"]`,
      );
      if (hidden) hidden.value = String(value ?? '');
    }

    return calculated;
  }

  evaluateCalculatorMathExpression(expression, values) {
    if (!expression) return 0;

    const normalized = expression.replace(/\{([^}]+)\}/g, (_match, token) => {
      const tokenText = String(token).trim();
      const key = tokenText.split(':').pop().trim();
      const value = values[key] ?? values[tokenText] ?? 0;
      const number = this.getCalculatorNumericValue(value);
      return Number.isFinite(number) ? String(number) : '0';
    });

    if (!/^[0-9+\-*/().\s]+$/.test(normalized)) return 0;

    return this.evaluateCalculatorTokens(this.tokenizeCalculatorExpression(normalized));
  }

  tokenizeCalculatorExpression(expression) {
    return expression.match(/\d+(?:\.\d+)?|[()+\-*/]/g) || [];
  }

  evaluateCalculatorTokens(tokens) {
    const precedence = { '+': 1, '-': 1, '*': 2, '/': 2 };
    const values = [];
    const operators = [];

    const applyOperator = () => {
      const operator = operators.pop();
      const right = values.pop() ?? 0;
      const left = values.pop() ?? 0;

      if (operator === '+') values.push(left + right);
      if (operator === '-') values.push(left - right);
      if (operator === '*') values.push(left * right);
      if (operator === '/') values.push(right === 0 ? 0 : left / right);
    };

    for (const token of tokens) {
      if (/^\d/.test(token)) {
        values.push(Number(token));
        continue;
      }

      if (token === '(') {
        operators.push(token);
        continue;
      }

      if (token === ')') {
        while (operators.length && operators[operators.length - 1] !== '(') {
          applyOperator();
        }
        operators.pop();
        continue;
      }

      while (
        operators.length &&
        operators[operators.length - 1] !== '(' &&
        precedence[operators[operators.length - 1]] >= precedence[token]
      ) {
        applyOperator();
      }
      operators.push(token);
    }

    while (operators.length) applyOperator();
    return values[0] ?? 0;
  }

  evaluateCalculatorCondition(calculator, conditionId) {
    const condition = (calculator.config.conditions || []).find(
      (item) => item.id === conditionId,
    );
    if (!condition) return true;

    const ruleResults = (condition.rules || []).map((rule) => {
      const value = calculator.values[rule.fieldId];
      return this.evaluateCalculatorRule(value, rule.operator, rule.value);
    });

    const matched =
      condition.logic === 'any'
        ? ruleResults.some(Boolean)
        : ruleResults.every(Boolean);

    return condition.action === 'hide' ? !matched : matched;
  }

  evaluateCalculatorRule(value, operator, expected) {
    if (operator === 'is') {
      if (Array.isArray(value)) return value.map(String).includes(String(expected));
      return String(value) === String(expected);
    }

    if (operator === 'isnot') {
      if (Array.isArray(value)) return !value.map(String).includes(String(expected));
      return String(value) !== String(expected);
    }

    if (operator === 'contains') {
      if (Array.isArray(value)) return value.map(String).includes(String(expected));
      return String(value).includes(String(expected));
    }

    const left = this.getCalculatorNumericValue(value);
    const right = this.getCalculatorNumericValue(expected);
    if (!Number.isFinite(left) || !Number.isFinite(right)) return false;

    if (operator === '>') return left > right;
    if (operator === '<') return left < right;
    if (operator === '>=') return left >= right;
    if (operator === '<=') return left <= right;
    return false;
  }

  getCalculatorNumericValue(value) {
    if (Array.isArray(value)) {
      const numbers = value
        .map((item) => this.getCalculatorNumericValue(item))
        .filter(Number.isFinite);
      return numbers.reduce((sum, number) => sum + number, 0);
    }

    if (typeof value === 'string') {
      const normalizedValue = value.trim().replace(',', '.');
      return Number(normalizedValue);
    }

    return Number(value);
  }

  renderCalculatorResults() {
    const calculator = this.calculators?.[0];
    if (!calculator) return;

    for (const resultBlock of this.calculatorResults) {
      const list = resultBlock.querySelector('[data-lp-calculator-results-list]');
      if (!list) continue;

      const allowedKeys = (resultBlock.dataset.lpCalculatorResultKeys || '')
        .split(',')
        .map((key) => key.trim())
        .filter(Boolean);
      const labelOverrides = this.parseCalculatorResultLabelOverrides(
        resultBlock.dataset.lpCalculatorResultLabels || '',
      );

      list.innerHTML = '';

      for (const formula of calculator.config.formulas || []) {
        const isAllowedResult = allowedKeys.includes(formula.key);
        if (!formula.display && !isAllowedResult) continue;
        if (allowedKeys.length && !isAllowedResult) continue;
        if (
          formula.conditionId &&
          !this.evaluateCalculatorCondition(calculator, formula.conditionId)
        ) {
          continue;
        }

        const item = document.createElement('div');
        item.className = 'lp-form__calculator-result';

        const label = document.createElement('span');
        label.className = 'lp-form__calculator-result-label';
        label.textContent =
          labelOverrides.get(formula.key) || formula.label || formula.key;

        const value = document.createElement('strong');
        value.className = 'lp-form__calculator-result-value';
        value.textContent = this.formatCalculatorValue(
          calculator.calculatedValues[formula.key],
          formula.format || 'number',
        );

        item.append(label, value);
        list.append(item);
      }
    }
  }

  parseCalculatorResultLabelOverrides(value) {
    const overrides = new Map();

    for (const line of value.split('\n')) {
      const separatorIndex = line.indexOf('=');
      if (separatorIndex <= 0) continue;

      const key = line.slice(0, separatorIndex).trim();
      const label = line.slice(separatorIndex + 1).trim();
      if (key && label) overrides.set(key, label);
    }

    return overrides;
  }

  formatCalculatorValue(value, format) {
    const number = Number(value);
    if (format === 'currency' && Number.isFinite(number)) {
      return new Intl.NumberFormat('fi-FI', {
        style: 'currency',
        currency: 'EUR',
      }).format(number);
    }

    if (Number.isFinite(number)) {
      return new Intl.NumberFormat('fi-FI', { maximumFractionDigits: 2 }).format(
        number,
      );
    }

    return String(value ?? '');
  }

  initializeChoiceGroups() {
    for (const group of this.querySelectorAll('[data-lp-choice-group]')) {
      const type = group.dataset.lpChoiceType || 'radio';
      const name = group.dataset.lpFieldKey;
      const required = group.dataset.lpFieldRequired === 'true';
      const label = group.dataset.lpFieldLabel || name;

      if (type === 'select') {
        this.initializeChoiceSelectGroup(group, { name, label, required });
        continue;
      }

      for (const input of group.querySelectorAll('[data-lp-choice]')) {
        input.type = type;
        input.name = type === 'checkbox' ? `${name}[]` : name;
        input.dataset.lpField = 'true';
        input.dataset.lpFieldKey = name;
        input.dataset.lpFieldLabel = label;
        input.dataset.lpFieldType = type;
        input.dataset.lpFieldRequired = String(required);
      }
    }
  }

  initializeChoiceSelectGroup(group, { name, label, required }) {
    const choices = Array.from(group.querySelectorAll('[data-lp-choice]'));
    const optionsContainer = group.querySelector('.lp-form__choice-options');
    const selectedChoice = choices.find((choice) => choice.checked);
    const placeholder = group.dataset.lpSelectPlaceholder || '';
    let select = group.querySelector('[data-lp-choice-select]');

    if (!select) {
      select = document.createElement('select');
      select.dataset.lpChoiceSelect = 'true';
      optionsContainer?.before(select);
    }

    select.className = 'lp-form__input';
    select.id = group.dataset.lpSelectId || select.id;
    select.name = name;
    select.required = required;
    select.dataset.lpField = 'true';
    select.dataset.lpFieldKey = name;
    select.dataset.lpFieldLabel = label;
    select.dataset.lpFieldType = 'select';
    select.dataset.lpFieldRequired = String(required);
    select.innerHTML = '';
    group.querySelector('[data-lp-choice-select-label]')?.setAttribute(
      'for',
      select.id,
    );

    const emptyOption = document.createElement('option');
    emptyOption.value = '';
    emptyOption.textContent = placeholder;
    emptyOption.selected = !selectedChoice;
    select.append(emptyOption);

    for (const choice of choices) {
      const option = document.createElement('option');
      const choiceLabel = choice
        .closest('.lp-form__choice-label')
        ?.querySelector('span')
        ?.textContent.trim();

      option.value = choice.value;
      option.textContent = choiceLabel || choice.value;
      option.selected = choice === selectedChoice;
      select.append(option);
    }

    if (optionsContainer) optionsContainer.hidden = true;
  }

  initializeConsents() {
    this.consentInputs = Array.from(
      this.querySelectorAll('[data-lp-consent="true"]'),
    );
    this.mainConsentInput = this.consentInputs.find(
      (input) => input.dataset.lpConsentMain === 'true',
    );

    for (const input of this.consentInputs) {
      input.addEventListener('change', () => this.updateConsentState());
    }

    this.updateConsentState();
  }

  initializeNumericInputs() {
    for (const input of this.querySelectorAll(
      '[data-lp-number-input="true"]',
    )) {
      input.addEventListener('input', () => {
        input.value = input.value.replace(/[^\d]/g, '');
      });
    }
  }

  initializeThemeEditorEvents() {
    if (!window.Shopify?.designMode) return;

    document.addEventListener('shopify:block:select', (event) => {
      if (!(event.target instanceof HTMLElement)) return;
      if (event.target.closest('lp-form-component') !== this) return;

      const submitPage = event.target.closest('[data-lp-submit-page]');
      if (!submitPage) return;

      this.showSubmitPage(submitPage.dataset.lpSubmitPageType, {
        page: submitPage,
        message: this.dataset.lpErrorMessage,
      });
    });
  }

  get hasSteps() {
    return this.pages.length > 1;
  }

  setPage(nextPage) {
    this.currentPage = Math.max(0, Math.min(nextPage, this.pages.length - 1));

    this.pages.forEach((page, index) => {
      page.toggleAttribute('hidden', index !== this.currentPage);
      page.dataset.lpFormPageActive = String(index === this.currentPage);
    });

    this.previousButton?.toggleAttribute('hidden', this.currentPage === 0);
    this.nextButton?.toggleAttribute(
      'hidden',
      this.currentPage >= this.pages.length - 1,
    );
    this.submitButton?.toggleAttribute(
      'hidden',
      this.currentPage < this.pages.length - 1,
    );
    this.dataset.lpActivePage = String(this.currentPage + 1);
    this.updateConsentState();
  }

  validateCurrentPage() {
    const scope = this.pages[this.currentPage];
    if (!scope) return true;

    const controls = Array.from(
      scope.querySelectorAll('input, select, textarea'),
    ).filter((control) => !control.closest('[data-lp-submit-pages]'));
    for (const group of scope.querySelectorAll(
      '[data-lp-choice-group][data-lp-field-required="true"]',
    )) {
      if (group.dataset.lpChoiceType === 'select') continue;

      const inputs = Array.from(group.querySelectorAll('input')).filter(
        (input) => !input.disabled,
      );
      const checked = inputs.some((input) => input.checked);
      const firstInput = inputs[0];

      for (const input of inputs) {
        input.setCustomValidity('');
      }

      if (firstInput) {
        firstInput.setCustomValidity(
          checked ? '' : 'Valitse vähintään yksi vaihtoehto.',
        );
      }
    }

    for (const control of controls) {
      if (!control.checkValidity()) {
        control.reportValidity();
        return false;
      }
    }

    return true;
  }

  async onSubmit(event) {
    event.preventDefault();
    this.hideSubmitPages();
    this.recalculateCalculators();

    if (this.hasSteps && this.currentPage < this.pages.length - 1) {
      if (this.validateCurrentPage()) this.setPage(this.currentPage + 1);
      return;
    }

    if (!this.validateCurrentPage()) {
      this.form.reportValidity();
      return;
    }

    if (!this.validateMainConsent()) {
      return;
    }

    const duplicateKeyReport = this.getDuplicateFieldKeyReport();
    if (duplicateKeyReport) {
      this.showError(duplicateKeyReport);
      return;
    }

    const honeypot = this.form.querySelector('[data-lp-honeypot]')?.value || '';
    const endpointConfig = this.getEndpointConfig();
    if (!endpointConfig) {
      this.showError(
        this.dataset.lpErrorMessage || 'Lomakkeen lähetys epäonnistui.',
      );
      return;
    }

    const payload = {
      storeId: endpointConfig.storeId,
      shop: endpointConfig.shop,
      site: window.location.hostname,
      formHandle: this.dataset.lpFormHandle || this.form.id,
      formName:
        this.dataset.lpFormName || this.dataset.lpFormHandle || this.form.id,
      pageUrl: window.location.href,
      pageTitle: document.title,
      fields: this.serializeFields(),
      fieldSchema: this.serializeFieldSchema(),
      honeypot,
    };

    await this.submit(payload, endpointConfig.submitUrl);
  }

  getFieldKeyEntries() {
    const controls = Array.from(this.form.querySelectorAll('[data-lp-field]'));
    const groupedChoiceKeys = new Set();

    return controls
      .filter(
        (control) =>
          !control.disabled && !control.closest('[data-lp-submit-pages]'),
      )
      .map((control) => {
        const key = control.dataset.lpFieldKey || control.name;
        if (!key) return null;
        const type =
          control.dataset.lpFieldType ||
          control.type ||
          control.tagName.toLowerCase();

        if (control.type === 'checkbox' || control.type === 'radio') {
          const groupKey = `${control.type}:${key}`;
          if (groupedChoiceKeys.has(groupKey)) return null;
          groupedChoiceKeys.add(groupKey);
        }

        return {
          key,
          label: control.dataset.lpFieldLabel || key,
          page:
            control.closest('[data-lp-form-page]')?.dataset.lpFormPage ||
            undefined,
          type,
        };
      })
      .filter(Boolean);
  }

  getDuplicateFieldKeyReport() {
    const entriesByKey = new Map();

    for (const entry of this.getFieldKeyEntries()) {
      if (!entriesByKey.has(entry.key)) entriesByKey.set(entry.key, []);
      entriesByKey.get(entry.key).push(entry);
    }

    const duplicates = Array.from(entriesByKey.entries()).filter(
      ([, entries]) => entries.length > 1,
    );
    if (!duplicates.length) return '';

    const details = duplicates
      .map(([key, entries]) => {
        const labels = entries
          .map((entry) => {
            const page = entry.page ? `sivu ${entry.page}` : 'tuntematon sivu';
            return `${entry.label} (${page})`;
          })
          .join(', ');
        return `${key}: ${labels}`;
      })
      .join('; ');

    return `Lomakkeessa on päällekkäisiä kenttäavaimia. Korjaa kenttien nimet ennen lähetystä: ${details}`;
  }

  getEndpointConfig() {
    const shop = window.Shopify?.shop;
    const shopConfig = LP_FORM_SHOP_CONFIG[shop];

    if (!shop || !shopConfig) return null;

    const proxyPath =
      this.dataset.lpUseDev === 'true'
        ? `${shopConfig.proxyPath}-dev`
        : shopConfig.proxyPath;

    return {
      shop,
      storeId: shopConfig.storeId,
      submitUrl: `${proxyPath}/api/forms`,
    };
  }

  serializeFields() {
    const fields = [];
    const grouped = new Map();
    const consentFields = new Map();
    const controls = Array.from(this.form.querySelectorAll('[data-lp-field]'));

    for (const control of controls) {
      const key = control.dataset.lpFieldKey || control.name;
      if (!key || control.disabled || control.closest('[data-lp-submit-pages]'))
        continue;

      const type =
        control.dataset.lpFieldType ||
        control.type ||
        control.tagName.toLowerCase();
      const label = control.dataset.lpFieldLabel || key;
      const page = control.closest('[data-lp-form-page]')?.dataset.lpFormPage;

      if (control.dataset.lpConsent === 'true') {
        consentFields.set(key, {
          key,
          label,
          type,
          value: control.checked,
          page: page ? Number(page) : undefined,
          required: control.dataset.lpFieldRequired === 'true',
          consentType: control.dataset.lpConsentType,
        });

        if (control.dataset.lpConsentMain === 'true' && control.checked) {
          this.addGrantedConsentFields(control, consentFields, page);
        }
        continue;
      }

      if (control.type === 'checkbox') {
        if (!grouped.has(key)) {
          grouped.set(key, {
            key,
            label,
            type,
            value: [],
            page: page ? Number(page) : undefined,
            required: control.dataset.lpFieldRequired === 'true',
          });
        }
        if (control.checked) grouped.get(key).value.push(control.value);
        continue;
      }

      if (control.type === 'radio') {
        if (grouped.has(key)) continue;
        const checked = controls.find(
          (item) =>
            item.type === 'radio' &&
            item.dataset.lpFieldKey === key &&
            item.checked,
        );
        grouped.set(key, {
          key,
          label,
          type,
          value: checked?.value || '',
          page: page ? Number(page) : undefined,
          required: control.dataset.lpFieldRequired === 'true',
        });
        continue;
      }

      fields.push({
        key,
        label,
        type,
        value: control.value,
        page: page ? Number(page) : undefined,
        required: control.required,
      });
    }

    return fields
      .concat(Array.from(grouped.values()))
      .concat(Array.from(consentFields.values()));
  }

  serializeFieldSchema() {
    return this.serializeFields().map(
      ({ key, label, type, required, page }) => ({
        key,
        label,
        type,
        required,
        page,
      }),
    );
  }

  addGrantedConsentFields(mainConsent, consentFields, page) {
    const grantedConsents = [
      ['email', 'email_consent', 'Sähköpostisuostumus'],
      ['sms', 'sms_consent', 'SMS-suostumus'],
      ['whatsapp', 'whatsapp_consent', 'WhatsApp-suostumus'],
    ];

    for (const [type, key, label] of grantedConsents) {
      if (
        mainConsent.dataset[`lpConsentGrants${this.toDatasetSuffix(type)}`] !==
        'true'
      ) {
        continue;
      }
      if (consentFields.has(key)) continue;

      consentFields.set(key, {
        key,
        label,
        type: 'consent',
        value: true,
        page: page ? Number(page) : undefined,
        required: false,
        consentType: type,
        source: 'form_consent',
      });
    }
  }

  toDatasetSuffix(value) {
    return value.charAt(0).toUpperCase() + value.slice(1);
  }

  hasAcceptedMainConsent() {
    return Boolean(this.mainConsentInput?.checked);
  }

  updateConsentState() {
    if (!this.submitButton) return;

    const canSubmit = this.hasAcceptedMainConsent();
    const isSubmitting = this.dataset.lpSubmitting === 'true';
    if (canSubmit) this.mainConsentInput?.setCustomValidity('');
    this.submitButton.toggleAttribute('disabled', !canSubmit || isSubmitting);
  }

  validateMainConsent() {
    if (!this.mainConsentInput) {
      this.showError('Lomakkeelta puuttuu pakollinen tietosuostumus.');
      return false;
    }

    this.mainConsentInput.setCustomValidity(
      this.mainConsentInput.checked
        ? ''
        : 'Hyväksy tietojen käsittely jatkaaksesi.',
    );

    if (this.mainConsentInput.checked) return true;

    const consentPage = this.pages.findIndex((page) =>
      page.contains(this.mainConsentInput),
    );
    if (consentPage >= 0) this.setPage(consentPage);
    this.mainConsentInput.reportValidity();
    return false;
  }

  setStatus(type, message) {
    if (!this.statusElement) return;
    this.statusElement.textContent = message || '';
    this.statusElement.dataset.lpFormStatusType = type;
    this.statusElement.toggleAttribute('hidden', !message);
  }

  hideSubmitPages() {
    delete this.dataset.lpSubmitState;
    this.submitPagesContainer?.setAttribute('hidden', '');
    this.submitPages.forEach((page) => page.setAttribute('hidden', ''));
  }

  showSubmitPage(type, options = {}) {
    const page =
      options.page ||
      (type === 'success' ? this.successPages[0] : this.errorPages[0]);

    if (!page) return false;

    this.pages.forEach((formPage) => formPage.setAttribute('hidden', ''));
    this.previousButton?.setAttribute('hidden', '');
    this.nextButton?.setAttribute('hidden', '');
    this.submitButton?.setAttribute('hidden', '');
    this.setStatus(type, '');

    this.submitPagesContainer?.removeAttribute('hidden');
    this.submitPages.forEach((submitPage) => {
      submitPage.toggleAttribute('hidden', submitPage !== page);
    });

    if (type === 'error') {
      const message =
        options.message ||
        this.dataset.lpErrorMessage ||
        'Lomakkeen lähetys epäonnistui.';
      this.errorMessageElements.forEach((element) => {
        element.textContent = message;
      });
    }

    this.dataset.lpSubmitState = type;
    return true;
  }

  showError(message) {
    if (this.showSubmitPage('error', { message })) return;
    this.setStatus('error', message);
  }

  async submit(payload, submitUrl) {
    if (!submitUrl) {
      this.setStatus(
        'error',
        this.dataset.lpErrorMessage || 'Lomakkeen lähetys epäonnistui.',
      );
      return;
    }

    this.dataset.lpSubmitting = 'true';
    this.submitButton?.setAttribute('disabled', '');
    this.setStatus('loading', this.dataset.lpLoadingMessage || 'Lähetetään...');

    try {
      const response = await fetch(submitUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      });
      const result = await response.json().catch(() => ({}));

      if (!response.ok || result.success === false) {
        throw new Error(
          result.message ||
            this.dataset.lpErrorMessage ||
            `Form submit failed with ${response.status}`,
        );
      }

      this.form.reset();
      this.setPage(0);
      if (!this.showSubmitPage('success')) {
        this.setStatus(
          'success',
          this.dataset.lpSuccessMessage || 'Kiitos, lomake lähetettiin.',
        );
      }
      this.dispatchEvent(new CustomEvent('lp-form:success', { bubbles: true }));
    } catch (error) {
      console.error(error);
      const errorMessage =
        error.message ||
        this.dataset.lpErrorMessage ||
        'Lomakkeen lähetys epäonnistui.';
      this.showError(errorMessage);
      this.dispatchEvent(new CustomEvent('lp-form:error', { bubbles: true }));
    } finally {
      this.dataset.lpSubmitting = 'false';
      this.updateConsentState();
    }
  }
}

if (!customElements.get('lp-form-component')) {
  customElements.define('lp-form-component', LPFormComponent);
}
